(() => {
  const WINNING_LINES = [
    [0, 1, 2],
    [3, 4, 5],
    [6, 7, 8],
    [0, 3, 6],
    [1, 4, 7],
    [2, 5, 8],
    [0, 4, 8],
    [2, 4, 6]
  ];

  class ProductTicTacToe {
    constructor(root) {
      this.root = root;
      this.sectionId = root.dataset.sectionId;
      this.playerImage = root.dataset.playerImage;
      this.aiImage = root.dataset.aiImage;
      this.voucherPrefix = root.dataset.voucherPrefix || 'WIN-';
      this.voucherLength = Number(root.dataset.voucherLength || 6);
      this.oneWinPerDevice = root.dataset.oneWin === 'true';

      this.cells = [...root.querySelectorAll('[data-cell-index]')];
      this.status = root.querySelector('[data-ttt-status]');
      this.thinking = root.querySelector('[data-ttt-thinking]');
      this.modal = root.querySelector('[data-ttt-modal]');
      this.voucherCodeElement = root.querySelector(
        '[data-ttt-voucher-code]'
      );
      this.copyFeedback = root.querySelector(
        '[data-ttt-copy-feedback]'
      );
      this.confettiCanvas = root.querySelector(
        '[data-ttt-confetti]'
      );

      this.initialStatus = this.status
        ? this.status.textContent.trim()
        : 'Your turn — choose a square.';

      this.storageKey =
        `product_ttt_voucher_${this.sectionId}`;

      this.board = Array(9).fill(null);
      this.gameOver = false;
      this.aiIsThinking = false;
      this.lastFocusedElement = null;

      this.bindEvents();
      this.resetGame();
    }

    bindEvents() {
      this.cells.forEach((cell) => {
        cell.addEventListener('click', () => {
          const index = Number(cell.dataset.cellIndex);
          this.handlePlayerMove(index);
        });
      });

      const resetButton = this.root.querySelector(
        '[data-ttt-reset]'
      );

      if (resetButton) {
        resetButton.addEventListener('click', () => {
          this.resetGame();
        });
      }

      const playAgainButton = this.root.querySelector(
        '[data-ttt-play-again]'
      );

      if (playAgainButton) {
        playAgainButton.addEventListener('click', () => {
          this.closeModal();
          this.resetGame();
        });
      }

      this.root
        .querySelectorAll('[data-ttt-close]')
        .forEach((button) => {
          button.addEventListener('click', () => {
            this.closeModal();
          });
        });

      const copyButton = this.root.querySelector(
        '[data-ttt-copy]'
      );

      if (copyButton) {
        copyButton.addEventListener('click', () => {
          this.copyVoucher();
        });
      }

      const downloadButton = this.root.querySelector(
        '[data-ttt-download]'
      );

      if (downloadButton) {
        downloadButton.addEventListener('click', () => {
          this.downloadVoucher();
        });
      }

      document.addEventListener('keydown', (event) => {
        if (
          event.key === 'Escape' &&
          this.modal &&
          !this.modal.hidden
        ) {
          this.closeModal();
        }
      });
    }

    resetGame() {
      this.board = Array(9).fill(null);
      this.gameOver = false;
      this.aiIsThinking = false;

      if (this.status) {
        this.status.textContent = this.initialStatus;
      }

      if (this.thinking) {
        this.thinking.hidden = true;
      }

      this.cells.forEach((cell, index) => {
        cell.innerHTML = '';
        cell.disabled = false;

        cell.classList.remove(
          'is-player',
          'is-ai',
          'is-winning'
        );

        cell.setAttribute(
          'aria-label',
          `Empty square ${index + 1}`
        );
      });
    }

    handlePlayerMove(index) {
      if (
        this.gameOver ||
        this.aiIsThinking ||
        this.board[index] !== null
      ) {
        return;
      }

      this.placePiece(index, 'player');

      const result = this.getResult();

      if (result) {
        this.finishGame(result);
        return;
      }

      this.aiTurn();
    }

    placePiece(index, owner) {
      this.board[index] = owner;

      const cell = this.cells[index];
      const image = document.createElement('img');

      image.src =
        owner === 'player'
          ? this.playerImage
          : this.aiImage;

      image.alt =
        owner === 'player'
          ? 'Your product'
          : 'AI product';

      image.loading = 'eager';

      cell.appendChild(image);
      cell.disabled = true;

      cell.classList.add(
        owner === 'player'
          ? 'is-player'
          : 'is-ai'
      );

      cell.setAttribute(
        'aria-label',
        `${
          owner === 'player' ? 'Your' : 'AI'
        } product in square ${index + 1}`
      );
    }

    aiTurn() {
      this.aiIsThinking = true;

      if (this.thinking) {
        this.thinking.hidden = false;
      }

      if (this.status) {
        this.status.textContent = 'AI turn...';
      }

      this.setBoardDisabled(true);

      window.setTimeout(() => {
        const move = this.chooseModerateMove();

        if (move !== null) {
          this.placePiece(move, 'ai');
        }

        if (this.thinking) {
          this.thinking.hidden = true;
        }

        this.aiIsThinking = false;

        const result = this.getResult();

        if (result) {
          this.finishGame(result);
          return;
        }

        if (this.status) {
          this.status.textContent = this.initialStatus;
        }

        this.setBoardDisabled(false);
      }, 650);
    }

    /*
      Balanced AI:
      - Takes its own winning move.
      - Blocks the player 68% of the time.
      - Prefers the center 55% of the time.
      - Prefers a corner 55% of the time.
      - Still makes random moves, so customers can win.
    */
    chooseModerateMove() {
      const emptyCells = this.getEmptyCells();

      if (!emptyCells.length) {
        return null;
      }

      const winningMove =
        this.findImmediateMove('ai');

      if (winningMove !== null) {
        return winningMove;
      }

      const blockingMove =
        this.findImmediateMove('player');

      if (
        blockingMove !== null &&
        Math.random() < 0.68
      ) {
        return blockingMove;
      }

      if (
        this.board[4] === null &&
        Math.random() < 0.55
      ) {
        return 4;
      }

      const availableCorners = [0, 2, 6, 8].filter(
        (index) => this.board[index] === null
      );

      if (
        availableCorners.length &&
        Math.random() < 0.55
      ) {
        const randomCorner = Math.floor(
          Math.random() * availableCorners.length
        );

        return availableCorners[randomCorner];
      }

      const randomIndex = Math.floor(
        Math.random() * emptyCells.length
      );

      return emptyCells[randomIndex];
    }

    findImmediateMove(owner) {
      const emptyCells = this.getEmptyCells();

      for (const index of emptyCells) {
        const testBoard = [...this.board];

        testBoard[index] = owner;

        const winningLine = this.getWinningLine(
          testBoard,
          owner
        );

        if (winningLine) {
          return index;
        }
      }

      return null;
    }

    getEmptyCells() {
      return this.board
        .map((value, index) => {
          return value === null ? index : null;
        })
        .filter((index) => index !== null);
    }

    getWinningLine(board, owner) {
      return (
        WINNING_LINES.find((line) => {
          return line.every((index) => {
            return board[index] === owner;
          });
        }) || null
      );
    }

    getResult() {
      const playerLine = this.getWinningLine(
        this.board,
        'player'
      );

      if (playerLine) {
        return {
          type: 'player',
          line: playerLine
        };
      }

      const aiLine = this.getWinningLine(
        this.board,
        'ai'
      );

      if (aiLine) {
        return {
          type: 'ai',
          line: aiLine
        };
      }

      if (this.getEmptyCells().length === 0) {
        return {
          type: 'draw',
          line: []
        };
      }

      return null;
    }

    finishGame(result) {
      this.gameOver = true;
      this.setBoardDisabled(true);

      result.line.forEach((index) => {
        this.cells[index].classList.add(
          'is-winning'
        );
      });

      if (result.type === 'player') {
        if (this.status) {
          this.status.textContent =
            'You won! Your voucher is ready.';
        }

        window.setTimeout(() => {
          this.showVoucher();
        }, 550);

        return;
      }

      if (result.type === 'ai') {
        if (this.status) {
          this.status.textContent =
            'Almost! Restart and try again.';
        }

        return;
      }

      if (this.status) {
        this.status.textContent =
          'It is a draw. Restart and try again.';
      }
    }

    setBoardDisabled(disabled) {
      this.cells.forEach((cell, index) => {
        cell.disabled =
          disabled ||
          this.board[index] !== null;
      });
    }

    showVoucher() {
      let voucher = null;

      if (this.oneWinPerDevice) {
        try {
          voucher = localStorage.getItem(
            this.storageKey
          );
        } catch (error) {
          voucher = null;
        }
      }

      if (!voucher) {
        voucher = this.generateVoucher();

        if (this.oneWinPerDevice) {
          try {
            localStorage.setItem(
              this.storageKey,
              voucher
            );
          } catch (error) {
            console.warn(
              'Voucher could not be saved locally.',
              error
            );
          }
        }
      }

      if (this.voucherCodeElement) {
        this.voucherCodeElement.textContent =
          voucher;
      }

      if (this.copyFeedback) {
        this.copyFeedback.textContent = '';
      }

      this.openModal();
      this.launchConfetti();
    }

    generateVoucher() {
      const characters =
        'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';

      let randomPart = '';

      if (
        window.crypto &&
        window.crypto.getRandomValues
      ) {
        const randomValues =
          new Uint32Array(this.voucherLength);

        window.crypto.getRandomValues(
          randomValues
        );

        randomValues.forEach((value) => {
          randomPart +=
            characters[
              value % characters.length
            ];
        });
      } else {
        for (
          let index = 0;
          index < this.voucherLength;
          index += 1
        ) {
          randomPart +=
            characters[
              Math.floor(
                Math.random() *
                  characters.length
              )
            ];
        }
      }

      return `${this.voucherPrefix}${randomPart}`;
    }

    openModal() {
      if (!this.modal) {
        return;
      }

      this.lastFocusedElement =
        document.activeElement;

      this.modal.hidden = false;

      document.documentElement.style.overflow =
        'hidden';

      const copyButton =
        this.modal.querySelector(
          '[data-ttt-copy]'
        );

      if (copyButton) {
        copyButton.focus();
      }
    }

    closeModal() {
      if (!this.modal) {
        return;
      }

      this.modal.hidden = true;

      document.documentElement.style.overflow =
        '';

      if (
        this.lastFocusedElement &&
        typeof this.lastFocusedElement.focus ===
          'function'
      ) {
        this.lastFocusedElement.focus();
      }
    }

    async copyVoucher() {
      if (!this.voucherCodeElement) {
        return;
      }

      const code =
        this.voucherCodeElement.textContent.trim();

      if (!code) {
        return;
      }

      try {
        await navigator.clipboard.writeText(code);

        if (this.copyFeedback) {
          this.copyFeedback.textContent =
            'Voucher copied successfully.';
        }
      } catch (error) {
        const textArea =
          document.createElement('textarea');

        textArea.value = code;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.opacity = '0';

        document.body.appendChild(textArea);

        textArea.focus();
        textArea.select();

        document.execCommand('copy');

        textArea.remove();

        if (this.copyFeedback) {
          this.copyFeedback.textContent =
            'Voucher copied successfully.';
        }
      }
    }

    drawRoundedRect(
      context,
      x,
      y,
      width,
      height,
      radius
    ) {
      context.beginPath();

      context.moveTo(
        x + radius,
        y
      );

      context.lineTo(
        x + width - radius,
        y
      );

      context.quadraticCurveTo(
        x + width,
        y,
        x + width,
        y + radius
      );

      context.lineTo(
        x + width,
        y + height - radius
      );

      context.quadraticCurveTo(
        x + width,
        y + height,
        x + width - radius,
        y + height
      );

      context.lineTo(
        x + radius,
        y + height
      );

      context.quadraticCurveTo(
        x,
        y + height,
        x,
        y + height - radius
      );

      context.lineTo(
        x,
        y + radius
      );

      context.quadraticCurveTo(
        x,
        y,
        x + radius,
        y
      );

      context.closePath();
    }

    downloadVoucher() {
      if (!this.voucherCodeElement) {
        return;
      }

      const code =
        this.voucherCodeElement.textContent.trim();

      if (!code) {
        return;
      }

      const canvas =
        document.createElement('canvas');

      const context =
        canvas.getContext('2d');

      if (!context) {
        return;
      }

      canvas.width = 1200;
      canvas.height = 700;

      const pink = '#CE017A';
      const dark = '#222222';
      const gray = '#6F6F78';
      const lightBackground = '#F7F2F5';

      /*
        Background
      */
      context.fillStyle = lightBackground;

      context.fillRect(
        0,
        0,
        canvas.width,
        canvas.height
      );

      /*
        Card shadow
      */
      context.save();

      context.shadowColor =
        'rgba(0, 0, 0, 0.16)';

      context.shadowBlur = 35;
      context.shadowOffsetX = 0;
      context.shadowOffsetY = 18;

      this.drawRoundedRect(
        context,
        120,
        80,
        960,
        540,
        45
      );

      context.fillStyle = '#FFFFFF';
      context.fill();

      context.restore();

      /*
        White card
      */
      this.drawRoundedRect(
        context,
        120,
        80,
        960,
        540,
        45
      );

      context.fillStyle = '#FFFFFF';
      context.fill();

      /*
        Pink top line
      */
      this.drawRoundedRect(
        context,
        120,
        80,
        960,
        18,
        9
      );

      context.fillStyle = pink;
      context.fill();

      /*
        Celebration icon
      */
      context.textAlign = 'center';
      context.textBaseline = 'alphabetic';
      context.font =
        '70px Arial, sans-serif';

      context.fillText(
        '🎉',
        600,
        190
      );

      /*
        Heading
      */
      context.fillStyle = dark;
      context.font =
        '700 58px Arial, sans-serif';

      context.fillText(
        'YOU WON!',
        600,
        275
      );

      /*
        Description
      */
      context.fillStyle = gray;
      context.font =
        '30px Arial, sans-serif';

      context.fillText(
        'Show this unique voucher at the branch.',
        600,
        330
      );

      /*
        Voucher code box
      */
      this.drawRoundedRect(
        context,
        300,
        375,
        600,
        120,
        30
      );

      context.fillStyle = pink;
      context.fill();

      /*
        Voucher code
      */
      context.fillStyle = '#FFFFFF';
      context.font =
        '700 46px Arial, sans-serif';
      context.textBaseline = 'middle';

      context.fillText(
        code,
        600,
        435
      );

      /*
        Footer
      */
      context.textBaseline = 'alphabetic';
      context.fillStyle = gray;
      context.font =
        '24px Arial, sans-serif';

      context.fillText(
        'Terms and conditions apply.',
        600,
        565
      );

      /*
        Create PNG file
      */
      canvas.toBlob(
        async (blob) => {
          if (!blob) {
            return;
          }

          const fileName =
            `voucher-${code}.png`;

          const file = new File(
            [blob],
            fileName,
            {
              type: 'image/png'
            }
          );

          const isIOS =
            /iPad|iPhone|iPod/.test(
              navigator.userAgent
            ) ||
            (
              navigator.platform === 'MacIntel' &&
              navigator.maxTouchPoints > 1
            );

          const isMobile =
            /Android|iPhone|iPad|iPod/i.test(
              navigator.userAgent
            );

          /*
            Best mobile option:
            Open the native Share / Save menu.
          */
          if (
            isMobile &&
            navigator.share &&
            navigator.canShare &&
            navigator.canShare({
              files: [file]
            })
          ) {
            try {
              await navigator.share({
                title: 'Your Voucher',
                text: `Your voucher code is ${code}`,
                files: [file]
              });

              return;
            } catch (error) {
              if (error.name === 'AbortError') {
                return;
              }
            }
          }

          const imageUrl =
            URL.createObjectURL(blob);

          /*
            iPhone and iPad fallback:
            Open the image in a new tab.
            The customer can long-press and save it.
          */
          if (isIOS) {
            const newWindow = window.open(
              imageUrl,
              '_blank'
            );

            if (!newWindow) {
              window.location.href = imageUrl;
            }

            window.setTimeout(() => {
              URL.revokeObjectURL(imageUrl);
            }, 60000);

            return;
          }

          /*
            Android and desktop download.
          */
          const link =
            document.createElement('a');

          link.href = imageUrl;
          link.download = fileName;
          link.style.display = 'none';

          document.body.appendChild(link);

          link.click();
          link.remove();

          window.setTimeout(() => {
            URL.revokeObjectURL(imageUrl);
          }, 5000);
        },
        'image/png'
      );
    }

    launchConfetti() {
      if (!this.confettiCanvas) {
        return;
      }

      if (
        window.matchMedia(
          '(prefers-reduced-motion: reduce)'
        ).matches
      ) {
        return;
      }

      const canvas =
        this.confettiCanvas;

      const context =
        canvas.getContext('2d');

      if (!context) {
        return;
      }

      const pixelRatio =
        window.devicePixelRatio || 1;

      canvas.width =
        window.innerWidth * pixelRatio;

      canvas.height =
        window.innerHeight * pixelRatio;

      canvas.style.width =
        `${window.innerWidth}px`;

      canvas.style.height =
        `${window.innerHeight}px`;

      context.setTransform(
        pixelRatio,
        0,
        0,
        pixelRatio,
        0,
        0
      );

      const pieces = Array.from(
        { length: 110 },
        () => {
          return {
            x:
              Math.random() *
              window.innerWidth,

            y:
              -20 -
              Math.random() *
                window.innerHeight *
                0.35,

            width:
              5 +
              Math.random() * 7,

            height:
              8 +
              Math.random() * 10,

            speedY:
              2.5 +
              Math.random() * 4,

            speedX:
              -1.5 +
              Math.random() * 3,

            rotation:
              Math.random() *
              Math.PI,

            rotationSpeed:
              -0.12 +
              Math.random() * 0.24,

            hue:
              Math.floor(
                Math.random() * 360
              )
          };
        }
      );

      const startTime =
        performance.now();

      const draw = (currentTime) => {
        context.clearRect(
          0,
          0,
          window.innerWidth,
          window.innerHeight
        );

        pieces.forEach((piece) => {
          piece.x += piece.speedX;
          piece.y += piece.speedY;

          piece.rotation +=
            piece.rotationSpeed;

          context.save();

          context.translate(
            piece.x,
            piece.y
          );

          context.rotate(
            piece.rotation
          );

          context.fillStyle =
            `hsl(${piece.hue} 85% 62%)`;

          context.fillRect(
            -piece.width / 2,
            -piece.height / 2,
            piece.width,
            piece.height
          );

          context.restore();
        });

        if (
          currentTime - startTime <
          2800
        ) {
          requestAnimationFrame(draw);
        } else {
          context.clearRect(
            0,
            0,
            window.innerWidth,
            window.innerHeight
          );
        }
      };

      requestAnimationFrame(draw);
    }
  }

  const initializeGames = (
    scope = document
  ) => {
    const gameSections =
      scope.querySelectorAll(
        '[data-ttt-game]'
      );

    gameSections.forEach((root) => {
      if (
        root.dataset.tttInitialized ===
        'true'
      ) {
        return;
      }

      root.dataset.tttInitialized =
        'true';

      new ProductTicTacToe(root);
    });
  };

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      initializeGames();
    }
  );

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initializeGames(event.target);
    }
  );
})();